import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Step,
  StepLabel,
  Stepper,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FieldPath, FormProvider, Resolver, useForm, useWatch } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';
import {
  deadLoadSchema,
  jobNameSchema,
  liveLoadSchema,
  locationAddressSchema,
  logDebug,
  logError,
  poSchema,
  showToast,
  snowLoadSchema,
  stateSchema,
  unformatPhoneNumber,
  windLoadSchema,
} from '../../utils';

import { DateTime } from 'luxon';
import { CancelConfirmDialog } from '../../components';
import { useAppSelector } from '../../store/hooks';
import { DealerLocation, JobLocation, OrderStatus } from '../../types';
import { useDealerData } from '../dealer/user-dealer-data';
import { jobLocationHasData } from './job-location-shipping';
import { useManufacturerData } from '../manufacturer/use-manufacturer-data';
import { selectManageDealers, selectManageManufacturers } from '../user';
import StepOne from './new-order-wizard-step-one';
import StepThree from './new-order-wizard-step-three';
import StepTwo from './new-order-wizard-step-two';
import { useCreateOrderMutation, useValidatePOUniquenessQuery } from './orders-api-slice';

const jobLocationSchema = z
  .object({
    address1: z.string().optional(),
    address2: z.string().optional(),
    city: z.string().optional(),
    state: z.string().optional(),
    zip: z.string().optional(),
    latitude: z.number().optional(),
    longitude: z.number().optional(),
    validationStatus: z.enum(['none', 'validated', 'failed', 'override']).optional(),
    validationMode: z.enum(['address', 'coordinates']).optional(),
  })
  .optional();

const asceDataSchema = z
  .object({
    windLoadMph: z.number(),
    snowLoadPsf: z.number(),
    elevation: z.string().optional(),
    riskLevel: z.number(),
    isHurricaneWindDebrisZone: z.boolean().optional(),
    isSpecialWindZone: z.boolean().optional(),
    isNewHampshire_7_16: z.boolean().optional(),
    mriWindResults: z.record(z.string()).optional(),
  })
  .optional();

/**
 * Pickup orders must always record a jobsite location — the shipping address is the plant,
 * not the jobsite, so the location data needed for ASCE comes from here. When address
 * validation is overridden, an entered (unvalidated) jobsite is acceptable.
 */
const isPickupJobLocationSatisfied = (data: {
  deliveryType?: 'Delivery' | 'Pickup';
  addressValidationOverride?: boolean;
  jobLocation?: {
    address1?: string;
    city?: string;
    state?: string;
    zip?: string;
    latitude?: number;
    longitude?: number;
    validationStatus?: string;
  } | null;
}): boolean => {
  if (data.deliveryType !== 'Pickup') return true;
  if (data.addressValidationOverride) return jobLocationHasData(data.jobLocation);
  return data.jobLocation?.validationStatus === 'validated';
};

const orderFormSchema = z
  .object({
    dealerId: z.number({
      required_error: 'Dealer is required',
      invalid_type_error: 'Dealer must be a number',
    }),
    manufacturerId: z.number({
      required_error: 'Manufacturer is required',
      invalid_type_error: 'Manufacturer must be a number',
    }),
    po: poSchema,
    jobName: jobNameSchema,
    shippingAddress: locationAddressSchema.optional(),
    deliveryType: z.enum(['Delivery', 'Pickup']),
    buildingState: z.string().optional().default(''),
    liveLoad: liveLoadSchema,
    deadLoad: deadLoadSchema,
    windLoad: windLoadSchema,
    snowLoad: snowLoadSchema,
    confirmedLoadRating: z.boolean(),
    jobLocation: jobLocationSchema,
    asceData: asceDataSchema,
    addressValidationOverride: z.boolean().optional(),
    manualLoadRatingCertified: z.boolean().optional(),
  })
  .refine(data => stateSchema.safeParse(data.buildingState).success, {
    message: 'State is required',
    path: ['buildingState'],
  })
  .refine(
    data => {
      if (data.addressValidationOverride) {
        return data.windLoad > 0 && data.snowLoad >= 0 && data.manualLoadRatingCertified === true;
      }
      return data.windLoad > 0;
    },
    {
      message:
        'Address must be validated with ASCE load data, or manual override with certification is required',
      path: ['windLoad'],
    }
  )
  .refine(isPickupJobLocationSatisfied, {
    message: 'A jobsite location is required for Pickup orders.',
    path: ['jobLocation'],
  });

export type OrderFormData = {
  dealerId: number;
  manufacturerId: number;
  po: string;
  jobName: string;
  shippingAddress?: DealerLocation;
  deliveryType: 'Delivery' | 'Pickup';
  buildingState: string;
  liveLoad: number;
  deadLoad: number;
  windLoad: number;
  snowLoad: number;
  confirmedLoadRating: boolean;
  jobLocation?: {
    address1?: string;
    address2?: string;
    city?: string;
    state?: string;
    zip?: string;
    latitude?: number;
    longitude?: number;
    validationStatus?: 'none' | 'validated' | 'failed' | 'override';
    validationMode?: 'address' | 'coordinates';
  };
  asceData?: {
    windLoadMph: number;
    snowLoadPsf: number;
    elevation?: string;
    riskLevel: number;
    isHurricaneWindDebrisZone?: boolean;
    isSpecialWindZone?: boolean;
    isNewHampshire_7_16?: boolean;
    mriWindResults?: Record<string, string>;
  };
  addressValidationOverride?: boolean;
  manualLoadRatingCertified?: boolean;
};

interface NewOrderWizardProps {
  onClose?: () => void;
}

const NewOrderWizard: React.FC<NewOrderWizardProps> = ({ onClose }) => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [formModified, setFormModified] = useState(false);
  const [isOpen, setIsOpen] = useState(true);
  const [isStepComplete, setIsStepComplete] = useState(false);
  const [completedSteps, setCompletedSteps] = useState<Record<number, boolean>>({});
  const [poDuplicateError, setPoDuplicateError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Get current user manufacturer and dealer IDs
  const currentUser = useAppSelector(state => state.user.currentUser);
  const currentUserManufacturerId = currentUser?.manufacturerId;
  const currentUserDealerId = currentUser?.dealerId;
  const canManageManufacturers = useAppSelector(selectManageManufacturers);
  const canManageDealers = useAppSelector(selectManageDealers);

  // Use theme and media queries for responsive design
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isMediumScreen = useMediaQuery(theme.breakpoints.down('md'));

  const steps = ['Order Information', 'Shipping Information', 'Building Information'];

  // Get manufacturer data
  const { manufacturers, isLoading: isLoadingManufacturers } = useManufacturerData({
    isEditMode: false,
    currentUserManufacturerId,
  });

  const methods = useForm<OrderFormData>({
    resolver: zodResolver(orderFormSchema) as Resolver<OrderFormData>,
    defaultValues: {
      dealerId: 0,
      manufacturerId: 0,
      po: '',
      jobName: '',
      shippingAddress: undefined,
      deliveryType: 'Pickup',
      buildingState: '',
      liveLoad: 10,
      deadLoad: 10,
      windLoad: 0,
      snowLoad: 0,
      confirmedLoadRating: false,
      jobLocation: undefined,
      asceData: undefined,
      addressValidationOverride: false,
      manualLoadRatingCertified: false,
    },
    mode: 'onChange',
    criteriaMode: 'all',
  });

  const { trigger, reset, formState, getValues, setValue, setError, clearErrors } = methods;

  // Watch manufacturerId to use in the dealer data hook
  const watchedManufacturerId = useWatch({ control: methods.control, name: 'manufacturerId' });

  // Watch for PO and dealerId for validation
  const watchedDealerId = useWatch({ control: methods.control, name: 'dealerId' });
  const watchedPO = useWatch({ control: methods.control, name: 'po' });

  // Add the createOrder mutation
  const [createOrder, { isLoading: isSubmitting }] = useCreateOrderMutation();

  // Query to validate PO uniqueness
  const { data: poValidationData, isLoading: isCheckingPO } = useValidatePOUniquenessQuery(
    {
      dealerId: watchedDealerId || 0,
      po: watchedPO || '',
    },
    {
      skip: !watchedDealerId || !watchedPO || watchedDealerId === 0 || watchedPO.length < 2,
    }
  );

  // Check for PO duplicate when validation data is available
  useEffect(() => {
    // Skip validation if PO is too short or dealer is not selected
    if (!watchedPO || !watchedDealerId || watchedPO.length < 2 || watchedDealerId === 0) {
      // Clear error when input is invalid or empty
      if (!watchedPO || watchedPO.length < 2) {
        setPoDuplicateError(null);
        clearErrors('po');
      }
      return;
    }

    // Process validation data whenever it's available
    // This ensures we always check the latest validation result, even if it's cached
    if (poValidationData !== undefined) {
      if (poValidationData.isUnique === false) {
        const errorMessage = 'The PO# you entered already exists for this dealer.';
        setPoDuplicateError(errorMessage);
        setIsStepComplete(false);
        // Set error on the PO field - use shouldFocus to ensure it displays
        setError('po', { type: 'manual', message: errorMessage }, { shouldFocus: true });
      } else if (poValidationData.isUnique === true) {
        // Only clear error if we have confirmation it's unique
        setPoDuplicateError(null);
        clearErrors('po');
      }
    }
    // Note: We don't clear errors when data is undefined, as this could be a loading state
    // The error will persist until we get confirmation the PO is unique
  }, [poValidationData, watchedPO, watchedDealerId, setError, clearErrors]);

  // Get dealer data
  const { dealers, isLoading: isLoadingDealers } = useDealerData({
    manufacturerId: watchedManufacturerId,
    currentUserDealerId,
  });

  // Define validation schemas for each step
  const stepValidations = useMemo(() => {
    return {
      0: z.object({
        manufacturerId: z.number(),
        dealerId: z.number(),
        po: z.string().min(2),
        jobName: z.string().min(2),
      }),
      1: z
        .object({
          deliveryType: z.enum(['Delivery', 'Pickup']),
          sameAsBilling: z.boolean().optional(),
          shippingAddressSelect: z.string().optional(),
          shippingAddress: locationAddressSchema.optional(),
        })
        .refine(
          data => {
            // For Pickup, no shipping address needed
            if (data.deliveryType === 'Pickup') {
              return true;
            }

            // For Delivery with sameAsBilling checked, no separate shipping address needed
            if (data.deliveryType === 'Delivery' && data.sameAsBilling === true) {
              return true;
            }

            // For Delivery without sameAsBilling, need shipping address select
            if (data.deliveryType === 'Delivery' && data.sameAsBilling !== true) {
              return (
                typeof data.shippingAddressSelect === 'string' &&
                data.shippingAddressSelect.trim() !== ''
              );
            }

            return false;
          },
          {
            message: 'Please select a shipping address',
            path: ['shippingAddressSelect'],
          }
        ),
      2: z
        .object({
          deliveryType: z.enum(['Delivery', 'Pickup']).optional(),
          buildingState: z.string().optional().default(''),
          liveLoad: z.number().min(0.1, 'Must be a positive number'),
          deadLoad: z.number().min(0.1, 'Must be a positive number'),
          windLoad: z.number().int('Wind load must be a whole number').min(0),
          snowLoad: z.number().int('Snow load must be a whole number').min(0),
          confirmedLoadRating: z.boolean().refine(val => val, {
            message: 'Load rating must be confirmed',
          }),
          addressValidationOverride: z.boolean().optional(),
          manualLoadRatingCertified: z.boolean().optional(),
          jobLocation: z.any().optional(),
        })
        .refine(data => stateSchema.safeParse(data.buildingState).success, {
          message: 'State is required',
          path: ['buildingState'],
        })
        .refine(
          data => {
            if (data.addressValidationOverride) {
              return (
                data.windLoad > 0 && data.snowLoad >= 0 && data.manualLoadRatingCertified === true
              );
            }
            // Non-override: require a validated address with ASCE data
            return data.windLoad > 0 && data.jobLocation?.validationStatus === 'validated';
          },
          {
            message:
              'Address must be validated with ASCE load data, or manual override with certification is required',
            path: ['windLoad'],
          }
        )
        .refine(isPickupJobLocationSatisfied, {
          message: 'A jobsite location is required for Pickup orders.',
          path: ['jobLocation'],
        }),
    };
  }, []);

  // Unified function to check step validity using Zod schema
  const checkStepCompleteness = useCallback(() => {
    const values = getValues();

    // For step 0, also check PO validation
    if (activeStep === 0) {
      const isPODuplicate = poValidationData && poValidationData.isUnique === false;
      const isCurrentlyValidatingPO = isCheckingPO && watchedPO && watchedDealerId;

      if (isPODuplicate || isCurrentlyValidatingPO) {
        setIsStepComplete(false);
        return;
      }
    }

    // Use the Zod schema for validation
    try {
      stepValidations[activeStep as keyof typeof stepValidations].parse(values);
      setIsStepComplete(true);

      // Update completed steps
      if (!completedSteps[activeStep]) {
        setCompletedSteps(prev => ({ ...prev, [activeStep]: true }));
      }
    } catch (error) {
      logDebug('Step validation failed:', error);
      setIsStepComplete(false);
    }
  }, [
    activeStep,
    getValues,
    stepValidations,
    completedSteps,
    poValidationData,
    isCheckingPO,
    watchedPO,
    watchedDealerId,
  ]);

  useEffect(() => {
    const checkCurrentStepValidity = async () => {
      // Get current form values
      const values = getValues();

      // For step 0, also check PO validation
      if (activeStep === 0) {
        const isPODuplicate = poValidationData && poValidationData.isUnique === false;
        const isCurrentlyValidatingPO = isCheckingPO && watchedPO && watchedDealerId;

        if (isPODuplicate || isCurrentlyValidatingPO) {
          setIsStepComplete(false);
          return;
        }
      }

      // Use the Zod schema to validate the current step
      try {
        stepValidations[activeStep as keyof typeof stepValidations].parse(values);
        setIsStepComplete(true);
      } catch (error) {
        // If validation failed but step was previously completed, let's log the error
        if (completedSteps[activeStep]) {
          logError('Previously completed step now fails validation:', error);
        }
        setIsStepComplete(false);
      }
    };

    // Run the validation check
    checkCurrentStepValidity().catch(reason => logDebug('Error checking step validity:', reason));
  }, [
    activeStep,
    getValues,
    completedSteps,
    stepValidations,
    poValidationData,
    isCheckingPO,
    watchedPO,
    watchedDealerId,
  ]);

  useEffect(() => {
    // When changing steps, check if the step was previously completed
    // or if it meets the completion criteria
    if (completedSteps[activeStep]) {
      setIsStepComplete(true);
    } else {
      // When navigating between steps, validate the current step using Zod schema
      const values = getValues();

      // For step 0, also check PO validation
      if (activeStep === 0) {
        const isPODuplicate = poValidationData && poValidationData.isUnique === false;
        const isCurrentlyValidatingPO = isCheckingPO && watchedPO && watchedDealerId;

        if (isPODuplicate || isCurrentlyValidatingPO) {
          setIsStepComplete(false);
          return;
        }
      }

      try {
        stepValidations[activeStep as keyof typeof stepValidations].parse(values);
        setIsStepComplete(true);
      } catch (error) {
        logDebug('Step validation failed:', error);
        setIsStepComplete(false);
      }
    }
  }, [
    activeStep,
    formState,
    completedSteps,
    getValues,
    stepValidations,
    poValidationData,
    isCheckingPO,
    watchedPO,
    watchedDealerId,
  ]);

  // Define validation field paths for each step
  const stepFieldPaths = useMemo(() => {
    return {
      0: ['manufacturerId', 'dealerId', 'po', 'jobName'] as FieldPath<OrderFormData>[],
      1: 'deliveryType' as FieldPath<OrderFormData>,
      2: [
        'buildingState',
        'liveLoad',
        'deadLoad',
        'windLoad',
        'snowLoad',
        'confirmedLoadRating',
        'addressValidationOverride',
        'manualLoadRatingCertified',
        'jobLocation',
      ] as FieldPath<OrderFormData>[],
    };
  }, []);

  const handleNext = async () => {
    // Get validation scope for current step
    const validationScope = stepFieldPaths[activeStep as keyof typeof stepFieldPaths];

    // First determine if validation is needed
    const needsValidation = !completedSteps[activeStep];

    // Additional validation for step 0: check PO uniqueness
    if (activeStep === 0) {
      // Check if we're still validating
      if (isCheckingPO && watchedPO && watchedDealerId) {
        return;
      }

      // Check if PO is duplicate
      if (poDuplicateError || (poValidationData && poValidationData.isUnique === false)) {
        return;
      }
    }

    // Validate this step if needed
    let isStepValid = true;
    if (needsValidation) {
      isStepValid = await trigger(validationScope);

      // Mark current step as completed if valid
      if (isStepValid && !poDuplicateError) {
        setCompletedSteps(prev => ({ ...prev, [activeStep]: true }));
      }
    }

    // Only proceed if the step is valid
    if (isStepValid && !poDuplicateError) {
      // Check if this is the final step
      if (activeStep === steps.length - 1) {
        // Final step - validate using Zod schema and submit
        try {
          setSubmitError(null);
          const formData = getValues();

          // Validate the entire form data using the Zod schema
          const validatedData = orderFormSchema.parse(formData);

          // If validation passes, submit the form
          await onSubmit(validatedData);
        } catch (error) {
          logError('Form validation failed:', error);
          setSubmitError('Unable to submit the order. Please review all fields and try again.');
        }
      } else {
        // Process navigation to next step
        const nextStep = activeStep + 1;
        setActiveStep(nextStep);

        // Update step completeness state
        setIsStepComplete(completedSteps[nextStep] || false);

        // If the next step isn't already marked complete, check its completeness
        if (!completedSteps[nextStep]) {
          checkStepCompleteness();
        }
      }
    }
  };

  const handleBack = () => {
    // Create a local variable to track if the step should be complete
    let stepIsComplete;

    // Decrease the active step
    const newStep = activeStep - 1;

    // Get current form values
    const values = getValues();

    // Validate the step
    try {
      stepValidations[newStep as keyof typeof stepValidations].parse(values);
      stepIsComplete = true;
    } catch (error) {
      logDebug('Step validation failed:', error);
      stepIsComplete = false;
    }

    // Update both state changes together to avoid race conditions
    // This will ensure React processes both updates in the same batch
    setActiveStep(newStep);
    setIsStepComplete(stepIsComplete);

    // Also update completedSteps if needed
    if (stepIsComplete && !completedSteps[newStep]) {
      setCompletedSteps(prev => ({ ...prev, [newStep]: true }));
    }
  };

  const handleCancel = () => {
    if (formModified) {
      setCancelDialogOpen(true);
    } else {
      handleClose();
    }
  };

  const handleCancelConfirm = () => {
    setCancelDialogOpen(false);
    handleClose();
  };

  const handleCancelDismiss = () => {
    setCancelDialogOpen(false);
  };

  const handleFormChange = useCallback(() => {
    if (!formModified) {
      setFormModified(true);
    }

    // When a form field changes in a previously completed step,
    // mark it as incomplete so it requires validation again
    if (completedSteps[activeStep]) {
      setCompletedSteps(prev => ({ ...prev, [activeStep]: false }));
    }

    // Use the Zod schema to validate the current step
    const values = getValues();

    try {
      stepValidations[activeStep as keyof typeof stepValidations].parse(values);
      setIsStepComplete(true);
    } catch (error) {
      logDebug('Step validation failed:', error);
      setIsStepComplete(false);
    }
  }, [formModified, completedSteps, activeStep, getValues, stepValidations]);

  // Internal handlers for onSubmit and onClose
  const onSubmit = async (data: OrderFormData) => {
    try {
      // Find the selected dealer from the dealers array
      const selectedDealer = dealers?.find(dealer => dealer.id === data.dealerId);

      const order = {
        orderNumber: 'UNKNOWN-' + DateTime.now().toMillis().toString(),
        orderDate: DateTime.now().toISO(),
        status: OrderStatus.DRAFT,

        dealerId: data.dealerId,
        po: data.po,
        buildingState: data.buildingState,
        jobName: data.jobName,
        liveLoad: data.liveLoad,
        deadLoad: data.deadLoad,
        windLoad: data.windLoad,
        snowLoad: data.snowLoad,
        confirmedLoadRating: data.confirmedLoadRating,
        pickup: data.deliveryType === 'Pickup',

        shippingAddress:
          data.deliveryType === 'Delivery'
            ? data.jobLocation?.address1
              ? {
                  address1: data.jobLocation.address1,
                  address2: data.jobLocation.address2 ?? null,
                  city: data.jobLocation.city ?? '',
                  state: data.jobLocation.state ?? '',
                  zip: data.jobLocation.zip ?? '',
                  phone: data.shippingAddress?.phone
                    ? unformatPhoneNumber(data.shippingAddress.phone) || null
                    : null,
                  fax: null,
                  email: data.shippingAddress?.email ?? null,
                }
              : data.shippingAddress
                ? {
                    address1: data.shippingAddress.address1,
                    address2: data.shippingAddress.address2 ?? null,
                    city: data.shippingAddress.city,
                    state: data.shippingAddress.state,
                    zip: data.shippingAddress.zip,
                    phone: data.shippingAddress.phone
                      ? unformatPhoneNumber(data.shippingAddress.phone) || null
                      : null,
                    fax: null,
                    email: data.shippingAddress.email ?? null,
                  }
                : undefined
            : undefined,

        billingAddress: {
          address1: selectedDealer!.address1,
          address2: selectedDealer!.address2 ?? null,
          city: selectedDealer!.city,
          state: selectedDealer!.state,
          zip: selectedDealer!.zip,
          phone: selectedDealer!.phone ? unformatPhoneNumber(selectedDealer!.phone) || null : null,
          fax: selectedDealer!.fax ? unformatPhoneNumber(selectedDealer!.fax) || null : null,
          email: selectedDealer!.email ?? null,
        },

        // New ASCE / Job Location fields
        jobLocation: data.jobLocation as JobLocation | undefined,
        asceData: data.asceData,
        addressValidationOverride: data.addressValidationOverride,
        manualLoadRatingCertified: data.manualLoadRatingCertified,
      };

      logDebug('Order created from form data:', order);

      const createdOrder = await createOrder({ orderData: order }).unwrap();
      showToast('Order successfully created', 'success');

      // Navigate to the order details page
      navigate(`/order-details/${createdOrder.id}`);

      handleClose();
    } catch (error) {
      logError('Error creating order:', error);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    reset();
    setActiveStep(0);
    setFormModified(false);
    setIsStepComplete(false);

    // Call the onClose prop if provided
    if (onClose) {
      onClose();
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <StepOne
            onFormChange={handleFormChange}
            manufacturers={manufacturers}
            isLoadingManufacturers={isLoadingManufacturers}
            dealers={dealers}
            isLoadingDealers={isLoadingDealers}
          />
        );
      case 1:
        return <StepTwo onFormChange={handleFormChange} dealers={dealers} />;
      case 2:
        return <StepThree onFormChange={handleFormChange} dealers={dealers} />;
      default:
        return null;
    }
  };

  // Effect to set the manufacturer ID and dealer ID based on user permissions
  useEffect(() => {
    // Set manufacturer ID to current user's manufacturer if they can't manage manufacturers
    if (!canManageManufacturers && currentUserManufacturerId) {
      setValue('manufacturerId', currentUserManufacturerId, {
        shouldDirty: false, // Don't mark the form as dirty
        shouldTouch: false, // Don't mark the field as touched
      });
    }

    // Set dealer ID to current user's dealer if they can't manage dealers
    if (!canManageDealers && currentUserDealerId) {
      setValue('dealerId', currentUserDealerId, {
        shouldDirty: false, // Don't mark the form as dirty
        shouldTouch: false, // Don't mark the field as touched
      });
    }
  }, [
    canManageManufacturers,
    currentUserManufacturerId,
    canManageDealers,
    currentUserDealerId,
    setValue,
  ]);

  const getButtonText = () => {
    if (activeStep === steps.length - 1) {
      return isSubmitting ? 'Submitting...' : 'Submit';
    }
    return 'Next';
  };

  const defaultWidth = isMediumScreen ? '90%' : '80%';
  const dialogWidth = isSmallScreen ? '100%' : defaultWidth;

  return (
    <>
      <Dialog
        open={isOpen}
        maxWidth="md"
        fullWidth
        fullScreen={isSmallScreen}
        aria-labelledby="add-order-dialog-title"
        PaperProps={{
          sx: {
            width: dialogWidth,
            maxWidth: theme.breakpoints.values.md,
            minHeight: isSmallScreen ? '100%' : '60vh',
            maxHeight: isSmallScreen ? '100%' : '60vh',
            display: 'flex',
            flexDirection: 'column',
            m: isSmallScreen ? 0 : 2,
          },
        }}
      >
        <DialogTitle id="add-order-dialog-title">
          Add New Order - Step {activeStep + 1} of {steps.length}
        </DialogTitle>

        <DialogContent
          sx={{
            flex: 1,
            overflow: 'auto',
            p: theme.spacing(3),
            minHeight: isSmallScreen ? 'auto' : '350px',
          }}
        >
          <Box sx={{ mb: 4 }}>
            <Stepper activeStep={activeStep}>
              {steps.map(label => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>

          <FormProvider {...methods}>
            <form onChange={handleFormChange}>{renderStepContent()}</form>
          </FormProvider>
        </DialogContent>

        {submitError && (
          <Alert severity="error" sx={{ mx: 3, mb: 1 }} onClose={() => setSubmitError(null)}>
            {submitError}
          </Alert>
        )}

        <DialogActions sx={{ p: theme.spacing(2, 3) }}>
          <Button onClick={handleCancel} color="error" disabled={isSubmitting}>
            Cancel
          </Button>
          <Box sx={{ flex: '1 1 auto' }} />
          {activeStep > 0 && (
            <Button onClick={handleBack} disabled={isSubmitting}>
              Back
            </Button>
          )}
          <Button
            onClick={handleNext}
            variant="contained"
            color="primary"
            disabled={!isStepComplete || isSubmitting}
          >
            {getButtonText()}
          </Button>
        </DialogActions>
      </Dialog>

      <CancelConfirmDialog
        open={cancelDialogOpen}
        onConfirm={handleCancelConfirm}
        onDismiss={handleCancelDismiss}
      />
    </>
  );
};

export default NewOrderWizard;
